import type { IframePoolOrderItem } from '../../../modules/api';
import { OpenedItemCard } from '../cards/OpenedItemCard';
import { EmptyState } from '../EmptyState';

export interface DropsTabProps {
  items: IframePoolOrderItem[];
  loading: boolean;
  onBrowse: () => void;
  /** Tap on a card toggles its membership in the selection set. */
  onItemClick?: (item: IframePoolOrderItem) => void;
  /** Tap the Buyback pill → instant sell, no modal. */
  onItemSell?: (item: IframePoolOrderItem) => Promise<void> | void;
  /** Item ids currently in the selection set — drives per-card visuals. */
  selectedIds?: ReadonlySet<string>;
}

/**
 * Drops-tab grid — opened pool_items rendered as a responsive grid of
 * glass-pedestal cards. The UI label of this tab is "Cards" per the
 * Figma 706:1683 redesign; the internal name stays `Drops` to match
 * the backend term and existing call sites.
 */
export function DropsTab({
  items,
  loading,
  onBrowse,
  onItemClick,
  onItemSell,
  selectedIds,
}: DropsTabProps) {
  if (loading && items.length === 0) {
    return (
      <div className="grid grid-cols-2 gap-3 @[600px]:gap-4 @[600px]:grid-cols-2 @[900px]:grid-cols-3 @[1200px]:grid-cols-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="
              h-[460px] rounded-[32px] animate-pulse
              border-2 border-[rgba(171,174,179,0.24)]
              bg-white/5
            "
          />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState title="No Cards Found" cta="Buy Repackz" onAction={onBrowse} />
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 @[600px]:gap-4 @[600px]:grid-cols-2 @[900px]:grid-cols-3 @[1200px]:grid-cols-4">
      {items.map((item) => (
        <OpenedItemCard
          key={item.id}
          item={item}
          selected={selectedIds?.has(item.id) ?? false}
          {...(onItemClick ? { onClick: onItemClick } : {})}
          {...(onItemSell ? { onSell: onItemSell } : {})}
        />
      ))}
    </div>
  );
}
